import { Component, Input, OnInit } from '@angular/core'
import { DomSanitizer } from '@angular/platform-browser'
import { select, Store } from '@ngrx/store'
import { IncidentResponse, selectIncidentsFromMessage } from '@tokeet-frontend/incident'
import { RejectTask, selectTasksByIncidentIds, TaskResponse } from '@tokeet-frontend/tasks'
import { ConfirmDialogService, Destroyable, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { GetMessagesByIds } from '@tv3/store/message/message.actions'
import { Message } from '@tv3/store/message/message.model'
import { selectMessagesByIds } from '@tv3/store/message/message.selectors'
import { OpenEditIncidentDialog, OpenEditTaskDialog } from '@tv3/store/overlay/overlay.actions'
import { combineLatest } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'

@Component({
  selector: 'app-message-draft-tasks',
  templateUrl: './message-draft-tasks.component.html',
  styleUrls: ['./message-draft-tasks.component.scss'],
})
export class MessageDraftTasksComponent extends Destroyable implements OnInit {
  @Input() inquiryId: string
  items: {
    incident: IncidentResponse
    message: Message
    task: TaskResponse
  }[] = []
  get count() {
    return this.items.length
  }

  constructor(private store: Store<any>, private confirmDialog: ConfirmDialogService, private sanitizer: DomSanitizer) {
    super()
  }

  ngOnInit(): void {
    this.store
      .select(selectIncidentsFromMessage)
      .pipe(
        map((incidents) =>
          this.inquiryId ? incidents.filter((incident) => incident.inquiry_id === this.inquiryId) : incidents
        ),
        tap((incidents) =>
          this.store.dispatch(GetMessagesByIds({ messageIds: incidents.map((incident) => incident.message_id) }))
        ),
        switchMap((incidents) => {
          return combineLatest([
            this.store.select(selectMessagesByIds(incidents.map((incident) => incident.message_id))),
            this.store.pipe(
              select(selectTasksByIncidentIds(incidents.map((incident) => incident.pkey))),
              map((tasks) => tasks.filter((task) => task.draft))
            ),
          ]).pipe(map(([messages, drafts]) => ({ incidents, messages, drafts })))
        }),
        untilDestroy(this)
      )
      .subscribe(({ incidents, messages, drafts }) => {
        this.items = messages
          .map((message) => {
            const incident = incidents.find((incident) => incident.message_id === message.id)
            return {
              incident: incident,
              message: message,
              task: drafts.find((draft) => draft.incident_id === incident?.pkey),
              sanitizedMessageHtml: this.sanitize(message?.messageHtml || ''),
            }
          })
          .filter((item) => !!item.incident && !!item.message && !!item.task)
      })
  }

  sanitize(html: string) {
    return this.sanitizer.bypassSecurityTrustHtml(html)
  }

  onIncident(incident: IncidentResponse) {
    this.store.dispatch(OpenEditIncidentDialog({ incidentId: incident.pkey }))
  }

  onViewTask(task: TaskResponse) {
    this.store.dispatch(OpenEditTaskDialog({ taskId: task.pkey }))
  }

  onRejectTask(task: TaskResponse) {
    this.confirmDialog
      .confirm({
        title: $localize`Reject Task`,
        body: $localize`Are you sure you want to reject this task?`,
      })
      .subscribe(() => {
        this.store.dispatch(RejectTask({ taskId: task.pkey }))
      })
  }

  onApproveTask(task: TaskResponse) {
    this.store.dispatch(OpenEditTaskDialog({ taskId: task.pkey }))
  }
}
